import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { EHttpService, EServerPath } from '~/models/api'
import HttpService from '~/services/http'

function UnsubscribePage() {
  const [searchParams] = useSearchParams()
  const [isUnsubscribed, setIsUnsubscribed] = useState(false)
  const theme = useTheme()

  const token = searchParams.get('token') || ''

  const unsubscribeMutation = useMutation({
    mutationFn: async () => {
      if (!token) {
        throw new Error('Invalid unsubscribe token')
      }
      return HttpService.post<{ success: boolean }>(
        {
          path: EServerPath.unsubscribeRecipient,
          payload: { token },
        },
        EHttpService.internal,
      )
    },
    onSuccess: () => {
      setIsUnsubscribed(true)
      toast.success('You have been successfully unsubscribed from our emails.')
    },
    onError: () => {
      toast.error(
        'There was an error processing your request. Please try again.',
      )
    },
  })

  const handleUnsubscribe = () => {
    if (!token) {
      toast.error('Invalid unsubscribe link. Please contact support.')
      return
    }
    unsubscribeMutation.mutate()
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.grey[50],
        padding: 2,
      }}
    >
      <Container maxWidth="sm">
        <Card elevation={3} sx={{ textAlign: 'center' }}>
          <CardHeader
            title="Email Unsubscribe"
            subheader="Do you want to unsubscribe from our emails?"
          />
          <CardContent>
            <Stack spacing={3} alignItems="center" textAlign="center">
              {!isUnsubscribed ? (
                <>
                  {!token && (
                    <Alert severity="warning" sx={{ textAlign: 'left' }}>
                      Invalid unsubscribe link. Please contact support if you
                      need assistance.
                    </Alert>
                  )}

                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleUnsubscribe}
                    disabled={unsubscribeMutation.isPending || !token}
                    startIcon={
                      unsubscribeMutation.isPending && (
                        <CircularProgress size={20} />
                      )
                    }
                  >
                    {unsubscribeMutation.isPending
                      ? 'Unsubscribing...'
                      : 'Unsubscribe'}
                  </Button>

                  <Typography variant="caption" color="text.secondary">
                    Once you unsubscribe, you will no longer receive marketing
                    emails from us.
                  </Typography>
                </>
              ) : (
                <Stack spacing={2} alignItems="center">
                  <Alert severity="success" sx={{ width: '100%' }}>
                    You have been successfully unsubscribed from our emails.
                  </Alert>
                  <Typography variant="body2" color="text.secondary">
                    You will no longer receive marketing emails from us.
                  </Typography>
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </Box>
  )
}

export default UnsubscribePage
